import { inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';

export interface MyApplicationClient {
    name: string;
    description: string | null;
    /** User-facing entry point of the client — opened in a new tab. */
    address: string;
}

export interface MyApplicationTenancy {
    tenantId: string;
    tenantName: string;
    /** RAW expiry instant (ISO string) — null = no expiry stamped (active), future = active until then. */
    deactivationDate: string | null;
    isApplicationSuperAdmin: boolean;
    lastLoginDate: string | null;
}

export interface MyApplication {
    applicationId: string;
    /** True when the user is application super-admin in ANY of their tenancies for this application. */
    isApplicationSuperAdmin: boolean;
    /** The user's most recent login to this application across tenancies (ISO string), if any. */
    lastLoginDate: string | null;
    /**
     * Per-tenancy breakdown of the aggregate. EMPTY in TWO distinct cases: an application available without
     * tenancy (every user), or a super-admin's default entry for a tenancy-REQUIRING application nobody leased
     * to them — disambiguate with `application.isRequiringTenancy` (see `accessKind`).
     */
    tenancies: MyApplicationTenancy[];
    application: {
        id: string;
        name: string;
        description: string | null;
        isRequiringTenancy: boolean;
    };
    clients: MyApplicationClient[];
}

export type MyApplicationAccessKind = 'tenancies' | 'availableToAll' | 'superAdminGrant';

/**
 * How the user has access to this entry. An empty tenancies list alone does NOT mean "available to everyone" —
 * a global super-admin also receives tenancy-REQUIRING applications they hold no lease on (empty tenancies);
 * those are access-by-clearance, not open applications.
 */
export function accessKind(app: Pick<MyApplication, 'tenancies'> & { application: { isRequiringTenancy: boolean } }): MyApplicationAccessKind {
    if (app.tenancies.length > 0) return 'tenancies';
    return app.application.isRequiringTenancy ? 'superAdminGrant' : 'availableToAll';
}

// Self-service: the backend guard is "any authenticated user" and only ever returns the caller's OWN
// lease-account data (a global super-admin receives every application). Activity semantics live server-side
// (DeactivationDate is an expiry; deactivated lease accounts / tenant memberships are already filtered out).
const MY_APPLICATIONS_QUERY = gql`
    query MojeAplikacije {
        myApplications {
            applicationId
            isApplicationSuperAdmin
            lastLoginDate
            tenancies {
                tenantId
                tenantName
                deactivationDate
                isApplicationSuperAdmin
                lastLoginDate
            }
            application {
                id
                name
                description
                isRequiringTenancy
            }
            clients {
                name
                description
                address
            }
        }
    }
`;

/**
 * Human-readable status of one tenancy, derived from the RAW expiry with the SAME token-issuance semantics as
 * `leaseUserStatus` (features/leases): null = active without expiry, future = active until then, elapsed =
 * deactivated since then. Dates are formatted for the sr-Latn-RS locale.
 */
export function tenancyStatus(deactivationDate: string | null, now: Date = new Date()): { active: boolean; label: string } {
    if (!deactivationDate) return { active: true, label: 'Aktivan' };
    const expiry = new Date(deactivationDate);
    const formatted = expiry.toLocaleString('sr-Latn-RS');
    return expiry > now ? { active: true, label: `Aktivan do ${formatted}` } : { active: false, label: `Deaktiviran od ${formatted}` };
}

/** Most recently used first (lastLoginDate desc, never-used last), then by application name. */
export function sortMyApplications(applications: readonly MyApplication[]): MyApplication[] {
    return [...applications].sort((a, b) => {
        const aTime = a.lastLoginDate ? new Date(a.lastLoginDate).getTime() : Number.NEGATIVE_INFINITY;
        const bTime = b.lastLoginDate ? new Date(b.lastLoginDate).getTime() : Number.NEGATIVE_INFINITY;
        if (aTime !== bTime) return bTime - aTime;
        return a.application.name.localeCompare(b.application.name, 'sr-Latn');
    });
}

@Injectable({ providedIn: 'root' })
export class MyApplicationsService {
    private readonly apollo = inject(Apollo);

    /** Returns the signed-in user's accessible applications, most recently used first. */
    async list(): Promise<MyApplication[]> {
        const result = await firstValueFrom(
            this.apollo.query<{ myApplications: MyApplication[] }>({
                query: MY_APPLICATIONS_QUERY,
                fetchPolicy: 'network-only'
            })
        );
        return sortMyApplications(result.data?.myApplications ?? []);
    }
}
